import {
  DomainSessionId,
  DomainUserId,
  SessionId,
  UserId,
} from '@hc-frontend/core-types';
import { GetSubsidyResponse } from '@hc-frontend/services';
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { Answers, AnswersObject } from './constants';
import {
  DOMAIN_SESSION_ID,
  DOMAIN_USER_ID,
  SESSION_ID,
  USER_ID,
} from './constants';

export type SubsidyCalculatorSessionParameters = {
  sessionId: SessionId;
  userId: UserId;
  domainSessionId: DomainSessionId | undefined;
  domainUserId: DomainUserId | undefined;
};

export type SubsidyCalculatorTrackingParameters = {
  SRC?: string;
  utm_campaign?: string;
  utm_medium?: string;
  utm_content?: string;
};

type SubsidyCalculatorContextType = {
  step: number;
  setStep: Dispatch<SetStateAction<number>>;
  answers: AnswersObject;
  setAnswer: <K extends Answers>(key: K, value: AnswersObject[K]) => void;
  subsidy?: GetSubsidyResponse;
  setSubsidy: Dispatch<SetStateAction<GetSubsidyResponse | undefined>>;
  reset: () => void;
  trackingParameters?: SubsidyCalculatorTrackingParameters;
  sessionParameters: SubsidyCalculatorSessionParameters;
};

const SubsidyCalculatorContext = createContext<
  SubsidyCalculatorContextType | undefined
>(undefined);

export const SubsidyCalculatorProvider = ({
  children,
  sessionParameters,
  trackingParameters,
}: {
  children: ReactNode;
  sessionParameters: SubsidyCalculatorSessionParameters;
  trackingParameters?: SubsidyCalculatorTrackingParameters;
}) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<AnswersObject>({});
  const [subsidy, setSubsidy] = useState<GetSubsidyResponse>();

  const setAnswer = useCallback((key: string, value: unknown) => {
    setAnswers((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const reset = useCallback(() => {
    setAnswers({
      [SESSION_ID]: sessionParameters.sessionId,
      [USER_ID]: sessionParameters.userId,
      [DOMAIN_SESSION_ID]: sessionParameters.domainSessionId,
      [DOMAIN_USER_ID]: sessionParameters.domainUserId,
    });

    setStep(0);
    setSubsidy(undefined);
  }, []);

  const value = useMemo(
    () => ({
      step,
      setStep,
      answers,
      setAnswer,
      subsidy,
      setSubsidy,
      reset,
      trackingParameters,
      sessionParameters,
    }),
    [
      step,
      answers,
      setAnswer,
      subsidy,
      reset,
      trackingParameters,
      sessionParameters,
    ],
  );

  return (
    <SubsidyCalculatorContext.Provider value={value}>
      {children}
    </SubsidyCalculatorContext.Provider>
  );
};

export const useSubsidyCalculator = () => {
  const context = useContext(SubsidyCalculatorContext);

  if (context === undefined) {
    throw new Error(
      'useSubsidyCalculator must be used within a SubsidyCalculatorProvider',
    );
  }

  return context;
};
